import type { Layout, UISchemaElement } from "@jsonforms/core";
import type { CaseReviewSubsection } from "../model/caseReview";
import { scopePath } from "./caseReviewLayout";

/** A subsection together with the ui-schema element that renders it. */
export interface CaseReviewSubsectionEntry extends CaseReviewSubsection { element: UISchemaElement }

interface GroupElement extends Layout { label?: string }

function isLayout(element: UISchemaElement): element is Layout {
  return Array.isArray((element as Layout).elements);
}

export function caseReviewGroupFields(element: UISchemaElement): string[] {
  // The key is the control's data path, dots and all: a field inside an object is addressed
  // `quality_control.qc_safety_date` by JSON Forms, by the drafts and by the findings.
  const path = element.type === "Control" ? scopePath((element as { scope?: string }).scope) : null;
  if (path) return [path];
  return isLayout(element) ? element.elements.flatMap(caseReviewGroupFields) : [];
}

function keyFor(label: string, index: number): string {
  const slug = label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  return `${slug || "group"}-${index}`;
}

/**
 * The subsections of a section are the `Group` elements of its ui schema, in order.
 * Controls that sit outside any Group form one implicit first subsection named after the
 * section, so no field can fall out of the navigation.
 */
export function caseReviewSubsections(uischema: UISchemaElement, sectionLabel: string): CaseReviewSubsectionEntry[] {
  if (!isLayout(uischema)) return [{ key: keyFor(sectionLabel, 0), label: sectionLabel, fieldKeys: caseReviewGroupFields(uischema), element: uischema }];
  const loose = uischema.elements.filter((element) => element.type !== "Group");
  const groups = uischema.elements.filter((element) => element.type === "Group") as GroupElement[];
  const entries: CaseReviewSubsectionEntry[] = [];
  if (loose.length) {
    const element: Layout = { type: "VerticalLayout", elements: loose };
    entries.push({ key: keyFor(sectionLabel, 0), label: sectionLabel, fieldKeys: loose.flatMap(caseReviewGroupFields), element });
  }
  groups.forEach((group, index) => {
    const label = group.label ?? `Group ${index + 1}`;
    entries.push({ key: keyFor(label, entries.length), label, fieldKeys: caseReviewGroupFields(group), element: group });
  });
  return entries;
}

/** A subsection whose only control is a record list (a Control carrying a `detail` layout). */
export function isRecordGroup(entry: CaseReviewSubsectionEntry): boolean {
  const hasDetail = (element: UISchemaElement): boolean => {
    if (element.type === "Control") return !!(element as { options?: { detail?: unknown } }).options?.detail;
    return isLayout(element) && element.elements.some(hasDetail);
  };
  return hasDetail(entry.element);
}
